import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

// Types
export type IncidentIOUser = {
  id: string;
  name: string;
  email: string;
  avatar_url?: string;
};

export type IncidentIOSchedule = {
  id: string;
  name: string;
  config?: any;
};

// Incident.io catalog type ID for Squads
const SQUADS_CATALOG_TYPE_ID = '01HKXGT98HRHBTK3G1AXCWJQT4';

// Dynamic window sizes for finding next on-call (in days)
const LOOKUP_WINDOWS_DAYS = [7, 30, 90];

// API Interface
export interface IncidentIOApi {
  getSchedule(scheduleId: string): Promise<IncidentIOSchedule>;
  findScheduleByName(teamName: string): Promise<string | null>;
  getCurrentAndNextOnCall(scheduleId: string): Promise<{
    current: IncidentIOUser[];
    next: IncidentIOUser[];
  }>;
}

// API Reference
export const incidentIOApiRef = createApiRef<IncidentIOApi>({
  id: 'plugin.incidentio.service',
});

// API Client Implementation
export class IncidentIOClient implements IncidentIOApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  static fromConfig(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
  }): IncidentIOClient {
    return new IncidentIOClient(options.discoveryApi, options.fetchApi);
  }

  private async fetch(path: string): Promise<Response> {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    const url = `${proxyUrl}/incident-io${path}`;

    return this.fetchApi.fetch(url);
  }

  async getSchedule(scheduleId: string): Promise<IncidentIOSchedule> {
    const response = await this.fetch(`/v2/schedules/${scheduleId}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch schedule: ${response.statusText}`);
    }

    const data = await response.json();
    return data.schedule;
  }

  async findScheduleByName(teamName: string): Promise<string | null> {
    try {
      const response = await this.fetch('/v2/schedules?page_size=100');

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const schedules = data.schedules || [];
      const teamNameLower = teamName.toLowerCase();

      // Step 1: Find schedule matching team name with "Primary"
      const directMatch = schedules.find(
        (s: any) =>
          s.name.toLowerCase().includes(teamNameLower) &&
          s.name.toLowerCase().includes('primary'),
      );

      if (directMatch) {
        return directMatch.id;
      }

      // Step 2: Look up team's catalog entry ID in incident.io Squads
      const teamCatalogId = await this.findSquadCatalogEntryId(teamName);

      if (!teamCatalogId) {
        return null;
      }

      // Step 3: Find a "Primary" schedule whose team_ids include this team
      const groupSchedule = schedules.find(
        (s: any) =>
          s.name.toLowerCase().includes('primary') &&
          Array.isArray(s.team_ids) &&
          s.team_ids.includes(teamCatalogId),
      );

      if (groupSchedule) {
        return groupSchedule.id;
      }

      return null;
    } catch {
      return null;
    }
  }

  private async findSquadCatalogEntryId(
    teamName: string,
  ): Promise<string | null> {
    try {
      const response = await this.fetch(
        `/v3/catalog_entries?catalog_type_id=${SQUADS_CATALOG_TYPE_ID}&page_size=250`,
      );

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const entries = data.catalog_entries || [];
      const teamNameLower = teamName.toLowerCase();

      const match = entries.find(
        (e: any) => e.name.toLowerCase() === teamNameLower,
      );

      return match ? match.id : null;
    } catch {
      return null;
    }
  }

  async getCurrentAndNextOnCall(scheduleId: string): Promise<{
    current: IncidentIOUser[];
    next: IncidentIOUser[];
  }> {
    try {
      const now = new Date();
      const currentUsers: IncidentIOUser[] = [];
      const nextUsers: IncidentIOUser[] = [];

      // Try progressively larger windows to find next on-call
      for (const days of LOOKUP_WINDOWS_DAYS) {
        const windowEnd = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
        const entries = await this.fetchScheduleEntries(
          scheduleId,
          now,
          windowEnd,
        );

        if (!entries) {
          return { current: [], next: [] };
        }

        // Parse current on-call (only on first iteration)
        if (currentUsers.length === 0) {
          const nowISO = now.toISOString();
          for (const entry of entries) {
            if (
              entry.user &&
              entry.start_at <= nowISO &&
              entry.end_at > nowISO
            ) {
              const alreadyAdded = currentUsers.some(
                u => u.id === entry.user.id,
              );
              if (!alreadyAdded) {
                currentUsers.push({
                  id: entry.user.id,
                  name: entry.user.name,
                  email: entry.user.email,
                  avatar_url: undefined,
                });
              }
            }
          }
        }

        // Find next on-call users (not currently active)
        const currentIds = new Set(currentUsers.map(u => u.id));
        const nowISO = now.toISOString();

        for (const entry of entries) {
          if (entry.user && entry.start_at > nowISO) {
            if (!currentIds.has(entry.user.id)) {
              const alreadyAdded = nextUsers.some(u => u.id === entry.user.id);
              if (!alreadyAdded) {
                nextUsers.push({
                  id: entry.user.id,
                  name: entry.user.name,
                  email: entry.user.email,
                  avatar_url: undefined,
                });
                break;
              }
            }
          }
        }

        // If we found next users, no need to try larger windows
        if (nextUsers.length > 0) {
          break;
        }
      }

      return { current: currentUsers, next: nextUsers };
    } catch {
      return { current: [], next: [] };
    }
  }

  private async fetchScheduleEntries(
    scheduleId: string,
    start: Date,
    end: Date,
  ): Promise<any[] | null> {
    try {
      const startISO = start.toISOString();
      const endISO = end.toISOString();
      const response = await this.fetch(
        `/v2/schedule_entries?schedule_id=${scheduleId}&entry_window_start=${startISO}&entry_window_end=${endISO}`,
      );

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const final = data.schedule_entries?.final || [];

      // Sort by start_at
      final.sort((a: any, b: any) => a.start_at.localeCompare(b.start_at));

      return final;
    } catch {
      return null;
    }
  }
}
